import { supabase } from './supabase';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000/api';
const API_KEY = import.meta.env.VITE_API_KEY;
const TIMEOUT_MS = 15_000;

interface ApiRequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly headers?: Record<string, string>;
  readonly signal?: AbortSignal;
}

interface ApiResponse<T> {
  readonly data: T;
  readonly status: number;
}

export class ApiError extends Error {
  constructor(
    public readonly statusCode: number,
    message: string,
    public readonly path: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function getAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

async function buildHeaders(
  custom?: Record<string, string>,
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...custom,
  };
  if (API_KEY) headers['X-API-Key'] = API_KEY;
  const token = await getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function request<T>(path: string, opts: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
  const { method = 'GET', body, headers: customHeaders, signal } = opts;
  const url = `${API_BASE_URL}${path}`;
  const headers = await buildHeaders(customHeaders);

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), TIMEOUT_MS);
  // Bridge external aborts (e.g. React Query cancellation) to our controller.
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  try {
    const response = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    if (response.status === 401) {
      // Try refreshing the session once and retry. Mirrors mobile behaviour.
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      if (!refreshError && refreshData.session) {
        const retryHeaders = {
          ...headers,
          Authorization: `Bearer ${refreshData.session.access_token}`,
        };
        const retry = await fetch(url, {
          method,
          headers: retryHeaders,
          body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        if (!retry.ok) {
          throw new ApiError(retry.status, `Request failed: ${retry.status}`, path);
        }
        if (retry.status === 204) return { data: null as T, status: retry.status };
        const retryJson = (await retry.json()) as T;
        return { data: retryJson, status: retry.status };
      }
      throw new ApiError(401, 'Authentication failed', path);
    }

    if (!response.ok) {
      const errBody = await response.json().catch(() => null);
      const message =
        (errBody && typeof errBody === 'object' && 'message' in errBody && typeof (errBody as { message: unknown }).message === 'string'
          ? (errBody as { message: string }).message
          : null) ?? `Request failed: ${response.status}`;
      throw new ApiError(response.status, message, path);
    }

    if (response.status === 204) return { data: null as T, status: response.status };
    const result = (await response.json()) as T;
    return { data: result, status: response.status };
  } catch (err) {
    if (err instanceof ApiError) throw err;
    if ((err as Error).name === 'AbortError') {
      throw new ApiError(0, 'Request timed out', path);
    }
    throw new ApiError(0, (err as Error).message ?? 'Network error', path);
  } finally {
    window.clearTimeout(timeout);
  }
}

export const apiClient = {
  get: <T>(path: string, opts?: ApiRequestOptions) => request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: ApiRequestOptions) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts?: ApiRequestOptions) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, opts?: ApiRequestOptions) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  delete: <T>(path: string, opts?: ApiRequestOptions) => request<T>(path, { ...opts, method: 'DELETE' }),
} as const;
